import React, { useEffect, useState } from "react";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import { FaUserEdit } from "react-icons/fa";
import { FaEye } from "react-icons/fa6";
import { MdDeleteForever } from "react-icons/md";
import { SubTitle } from "../../Components/CustomComponents";
import DeleteModal from "../../Components/Modals/DeleteModal";
import EditUserModal from "../../Components/Modals/EditUserModal";
import ModalContainer from "../../Components/ModalContainer";
import Table from "../../Components/Table/Table";
import Toast from "../../Hooks/useToast";
import styles from "./styles/user_management.module.scss";
import { api_urls } from "../../utilities";
import { deleteService, getService, patchService, postService } from "../../redux/services";

import { useDispatch, useSelector } from "react-redux";
import { fetchArtists, fetchUsers } from "../../Data/features/userSlice";

function formatRole(row) {
  const raw = row.role ?? row.user_type;
  if (raw == null || raw === "") return "-";
  return String(raw)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const UserManagementScreen = () => {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.user.users);
  const artists = useSelector((state) => state.user.artists);
  const usersLoading = useSelector((state) => state.user.usersLoading);
  const artistsLoading = useSelector((state) => state.user.artistsLoading);

  const [indicationMessage, setIndicationMessage] = useState("loading...");
  const [rowData, setRowData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editedData, setEditedData] = useState("");
  const [blockTogglingId, setBlockTogglingId] = useState(null);

  const [deleteModal, setDeleteModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [shopActionLoading, setShopActionLoading] = useState(false);
  const [shopApprovalStatus, setShopApprovalStatus] = useState(null);

  const isLoading = usersLoading || artistsLoading;

  const loadData = () => {
    dispatch(fetchUsers());
    dispatch(fetchArtists());
  };

  useEffect(() => {
    loadData();
  }, [dispatch]);

  useEffect(() => {
    const combined = [...users, ...artists];
    setRowData(combined);
  }, [users, artists]);

  useEffect(() => {
    if (isLoading) {
      setIndicationMessage("loading...");
      return;
    }
    setIndicationMessage(rowData.length ? "" : "No users found");
  }, [isLoading, rowData.length]);

  const handleDeleteModalOpen = () => {
    setDeleteModal(true);
  };

  const handleDeleteModalClose = () => {
    setDeleteModal(false);
    setEditedData("");
  };

  const deleteData = async (id) => {
    if (!id) return;
    try {
      await deleteService(api_urls.userById(id));
      Toast("Deleted Successfully", "success");
      handleDeleteModalClose();
      loadData();
    } catch (error) {
      const msg =
        error?.data?.message ?? error?.message ?? "Failed to delete user";
      Toast(msg, "error");
    }
  };

  const handleEditModalOpen = () => {
    setEditModal(true);
  };

  const handleEditModalClose = () => {
    setEditModal(false);
    setEditedData("");
  };

  const handleViewModalOpen = async (row) => {
    setEditedData(row);
    setViewModal(true);

    if (isArtistUser(row)) {
      const userId = row.id;
      setShopApprovalStatus("loading");
      try {
        const response = await getService(api_urls.userShop(userId));
        const envelope = response?.data ?? {};
        const dataArr = envelope.data;
        setShopApprovalStatus(
          Array.isArray(dataArr) && dataArr.length === 0 ? "pending" : "approved"
        );
      } catch {
        setShopApprovalStatus(null);
      }
    } else {
      setShopApprovalStatus(null);
    }
  };

  const handleViewModalClose = () => {
    setViewModal(false);
    setEditedData("");
    setShopApprovalStatus(null);
  };

  const getUserName = (row) => {
    return (
      row.name ||
      row.username ||
      `${row.firstName || ""} ${row.lastName || ""}`.trim()
    );
  };

  const getPhoneNumber = (row) => {
    return row.mobile_no || row.phone || "-";
  };

  const getArtistPayload = (row) => {
    if (!row || typeof row !== "object") return null;
    return (
      row.artist ??
      row.artist_detail ??
      row.artist_details ??
      row.artistData ??
      row.artist_data ??
      row.profile ??
      null
    );
  };

  const getArtistShopPayload = (row) => {
    if (row && "shop" in row) return row.shop;
    if (row && "artist_shop" in row) return row.artist_shop;
    if (row && "shop_detail" in row) return row.shop_detail;
    if (row && "shop_details" in row) return row.shop_details;
    if (row && "artistShop" in row) return row.artistShop;
    const artistPayload = getArtistPayload(row);
    if (artistPayload && "shop" in artistPayload) return artistPayload.shop;
    if (artistPayload && "artist_shop" in artistPayload) return artistPayload.artist_shop;
    if (artistPayload && "shop_detail" in artistPayload) return artistPayload.shop_detail;
    if (artistPayload && "shop_details" in artistPayload) return artistPayload.shop_details;
    return undefined;
  };

  const getArtistShopId = (row) => {
    const artistPayload = getArtistPayload(row);
    return artistPayload?.id ?? row.artist_id ?? row.id;
  };

  const isArtistUser = (row) => {
    if (!row || typeof row !== "object") return false;
    const role = String(row.role ?? row.user_type ?? "").toLowerCase();
    return (
      role.includes("artist") ||
      row.artist_id != null ||
      getArtistPayload(row) != null
    );
  };

  const showShopActionButtons = () => {
    return shopApprovalStatus === "pending";
  };

  const USER_HIDDEN_KEYS = new Set([
    "firstname",
    "first_name",
    "lastname",
    "last_name",
    "image_url",
    "image",
    "avatar",
    "profile_image",
    "profile_picture",
    "artist",
    "artist_detail",
    "artist_details",
    "artist_data",
    "artistData",
    "profile",
    "shop",
    "artist_shop",
    "shop_detail",
    "shop_details",
    "artistShop",
  ]);

  const ARTIST_HIDDEN_KEYS = new Set([
    "image_url",
    "image",
    "avatar",
    "profile_image",
    "profile_picture",
    "shop",
    "artist_shop",
    "shop_detail",
    "shop_details",
    "artistShop",
  ]);

  const normalizeValue = (value) => {
    if (value == null || value === "") return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) {
      if (!value.length) return "-";
      return value
        .map((entry) => {
          if (entry == null || entry === "") return "-";
          if (typeof entry === "object") {
            return entry.name || entry.title || entry.label || JSON.stringify(entry);
          }
          return String(entry);
        })
        .join(", ");
    }
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const renderKeyValueRows = (record, hiddenKeys = new Set()) => {
    if (!record || typeof record !== "object") return [];
    return Object.entries(record)
      .filter(([key]) => !hiddenKeys.has(key.toLowerCase()) && !hiddenKeys.has(key))
      .map(([key, value]) => {
        if (value && typeof value === "object" && !Array.isArray(value)) {
          return null;
        }
        return (
          <div key={key} className={styles.detailItem}>
            <span className={styles.detailLabel}>
              {key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}
            </span>
            <span className={styles.detailValue}>{normalizeValue(value)}</span>
          </div>
        );
      });
  };

  const SHOP_HIDDEN_KEYS = new Set([
    "about",
    "price_option_id",
    "price_option",
    "location_id",
    "attachments",
    "shop_days",
    "shop_services",
    "shop_discounts",
    "image_url",
    "image",
  ]);

  const renderShopDetails = (shop) => {
    if (!shop || typeof shop !== "object") return null;

    const rows = Object.entries(shop)
      .filter(([key]) => !SHOP_HIDDEN_KEYS.has(key) && !SHOP_HIDDEN_KEYS.has(key.toLowerCase()))
      .map(([key, value]) => {
        if (value && typeof value === "object" && !Array.isArray(value)) return null;
        return (
          <div key={key} className={styles.detailItem}>
            <span className={styles.detailLabel}>
              {key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
            </span>
            <span className={styles.detailValue}>{normalizeValue(value)}</span>
          </div>
        );
      });

    const daysArr = shop.shop_days ?? shop.days ?? [];
    const daysEl =
      Array.isArray(daysArr) && daysArr.length ? (
        <div key="shop_days" className={styles.detailItem}>
          <span className={styles.detailLabel}>Shop Days</span>
          <div className={styles.chipsWrap}>
            {daysArr.map((d, i) => (
              <span key={d?.id ?? i} className={styles.chip}>
                {d?.day ?? String(d)}
              </span>
            ))}
          </div>
        </div>
      ) : null;

    const servicesArr = shop.shop_services ?? [];
    const servicesEl =
      Array.isArray(servicesArr) && servicesArr.length ? (
        <div key="shop_services" className={styles.detailItem}>
          <span className={styles.detailLabel}>Shop Services</span>
          <div className={styles.chipsWrap}>
            {servicesArr.map((s, i) => (
              <span key={s?.id ?? i} className={`${styles.chip} ${styles.chipGreen}`}>
                {s?.name ?? s?.title ?? String(s)}
              </span>
            ))}
          </div>
        </div>
      ) : null;

    const attachmentsArr = shop.attachments ?? [];
    const attachmentsEl =
      Array.isArray(attachmentsArr) && attachmentsArr.length ? (
        <div key="attachments" className={styles.attachmentsSection}>
          <span className={styles.detailLabel} style={{ display: "block", marginBottom: "10px" }}>
            Attachments
          </span>
          <div className={styles.attachmentsGrid}>
            {attachmentsArr.map((a, i) => {
              const src = a?.url ?? a?.image_url ?? a?.image ?? null;
              if (!src) return null;
              return (
                <a key={a?.id ?? i} href={src} target="_blank" rel="noreferrer">
                  <img
                    src={src}
                    alt={`attachment-${i + 1}`}
                    className={styles.attachmentThumb}
                    loading="lazy"
                  />
                </a>
              );
            })}
          </div>
        </div>
      ) : null;

    return (
      <>
        {rows}
        {daysEl}
        {servicesEl}
        {attachmentsEl}
      </>
    );
  };

  const handleArtistShopAction = async (actionType, row) => {
    const shopId = getArtistShopId(row);
    if (!shopId) {
      Toast("Shop id not found", "error");
      return;
    }
    const isApprove = actionType === "approve";
    const api = isApprove
      ? api_urls.artistShopApprove(shopId)
      : api_urls.artistShopReject(shopId);

    setShopActionLoading(true);
    try {
      const response = await postService(api, {}, true);
      const envelope = response?.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        const error = new Error(envelope.message ?? "Request failed");
        error.data = { message: envelope.message ?? "Request failed" };
        throw error;
      }
      Toast(
        isApprove ? "Artist shop approved successfully" : "Artist shop rejected successfully",
        "success"
      );
      handleViewModalClose();
      loadData();
    } catch (error) {
      const msg =
        error?.data?.message ??
        error?.message ??
        `Failed to ${isApprove ? "approve" : "reject"} artist shop`;
      Toast(msg, "error");
    } finally {
      setShopActionLoading(false);
    }
  };

  const handleToggleUserBlock = async (row) => {
    if (row?.id == null) return;
    const nextBlocked = !Boolean(row.is_blocked);
    setBlockTogglingId(row.id);
    try {
      const res = await patchService(
        api_urls.updateUser(row.id),
        { is_blocked: nextBlocked },
        true
      );
      const envelope = res?.data ?? {};
      const code = envelope.code;
      if (typeof code === "number" && code !== 200) {
        const err = new Error(envelope.message ?? "Update failed");
        err.data = { message: envelope.message ?? "Update failed" };
        throw err;
      }
      const updated = envelope.data;
      setRowData((prevRows) =>
        prevRows.map((item) => {
          if (item.id !== row.id) return item;
          if (updated && typeof updated === "object") {
            return { ...item, ...updated, is_blocked: nextBlocked };
          }
          return { ...item, is_blocked: nextBlocked };
        })
      );
      Toast(
        nextBlocked ? "User blocked" : "User unblocked",
        "success"
      );
    } catch (error) {
      const msg =
        error?.data?.message ??
        error?.message ??
        "Failed to update user";
      Toast(msg, "error");
    } finally {
      setBlockTogglingId(null);
    }
  };

  const filteredData = searchTerm.trim()
    ? rowData.filter((row) => {
        const q = searchTerm.toLowerCase();
        return (
          (row.name || "").toLowerCase().includes(q) ||
          (row.username || "").toLowerCase().includes(q) ||
          (row.email || "").toLowerCase().includes(q) ||
          (row.mobile_no || "").toLowerCase().includes(q) ||
          (row.phone || "").toLowerCase().includes(q) ||
          formatRole(row).toLowerCase().includes(q)
        );
      })
    : rowData;

  function indication() {
    return indicationMessage;
  }

  const columns = [
    {
      dataField: "username",
      text: "Username",
      sort: true,
      headerStyle: {
        width: "200px",
      },
      formatter: (cellContent, row) => {
        const imageUrl = row.image_url || row.image;
        const fallbackName = getUserName(row) || "User";

        return (
          <div className={styles.userCell}>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={fallbackName}
                className={styles.userAvatar}
                loading="lazy"
              />
            ) : null}
            <span className={styles.userNameText}>{fallbackName}</span>
          </div>
        );
      },
    },
    {
      dataField: "email",
      text: "Email",
      sort: true,
      headerStyle: {
        width: "260px",
      },
    },
    {
      dataField: "role",
      text: "Role",
      sort: true,
      headerStyle: {
        width: "130px",
      },
      formatter: (cellContent, row) => formatRole(row),
    },
    {
      dataField: "mobile_no",
      text: "Phone Number",
      sort: true,
      headerStyle: {
        width: "160px",
      },
      formatter: (cellContent, row) => getPhoneNumber(row),
    },
    {
      dataField: "is_blocked",
      text: "Block / Unblock",
      headerAlign: "center",
      align: "center",
      headerStyle: {
        width: "180px",
      },
      formatter: (cellContent, row) => {
        const isBlocked = Boolean(row.is_blocked);

        return (
          <label
            className={styles.switchWrapper}
            style={{
              opacity: blockTogglingId === row.id ? 0.6 : 1,
              pointerEvents: blockTogglingId === row.id ? "none" : undefined,
            }}
          >
            <input
              type="checkbox"
              checked={!isBlocked}
              disabled={blockTogglingId === row.id}
              onChange={() => handleToggleUserBlock(row)}
            />
            <span className={styles.slider}></span>
            <span className={styles.switchText}>
              {isBlocked ? "Blocked" : "Active"}
            </span>
          </label>
        );
      },
    },
    {
      dataField: "actions",
      text: "Actions",
      headerAlign: "center",
      align: "center",
      headerStyle: {
        width: "200px",
      },
      formatter: (cellContent, row) => {
        if (row.id) {
          return (
            <div className="d-flex justify-content-center align-items-center ">
              <div
                className={`${styles.viewBtnBg} d-flex justify-content-center align-items-center rounded-3 mx-2 p-2 `}
                role="button"
                onClick={() => handleViewModalOpen(row)}
              >
                <span
                  className={`align-items-center d-flex justify-content-center pe-1 `}
                >
                  <FaEye />
                </span>
                <span>View</span>
              </div>
              <div
                className={`${styles.btnBg} d-flex justify-content-center align-items-center rounded-3 mx-2 p-2 `}
                role="button"
                onClick={() => {
                  setEditedData(row);
                  handleEditModalOpen(true);
                }}
              >
                <span
                  className={`align-items-center d-flex justify-content-center pe-1 `}
                >
                  <FaUserEdit />
                </span>
                <span>Edit</span>
              </div>
              <div
                className="d-flex justify-content-center align-items-center bg-danger rounded-3 p-2 mx-2 "
                role="button"
                onClick={() => {
                  setEditedData(row);
                  handleDeleteModalOpen();
                }}
              >
                <span className={`d-flex align-items-center pe-1`}>
                  <MdDeleteForever />
                </span>
                <span>Delete</span>
              </div>
            </div>
          );
        }
      },
    },
  ];

  return (
    <div className={styles.MainContainer}>
      {deleteModal ? (
        <DeleteModal
          handleDeleteModalClose={handleDeleteModalClose}
          data={editedData}
          onDelete={deleteData}
        />
      ) : (
        ""
      )}

      {editModal ? (
        <EditUserModal
          handleEditModalClose={handleEditModalClose}
          data={editedData}
        />
      ) : (
        ""
      )}

      {viewModal ? (
        <ModalContainer handleClose={handleViewModalClose}>
          <div className={styles.viewModalContainer}>
            <div className={styles.viewModalHeader}>
              <h4 className="m-0">User Details</h4>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={handleViewModalClose}
              >
                ✕
              </button>
            </div>

            {(() => {
              const imgSrc =
                editedData?.image_url ||
                editedData?.image ||
                editedData?.avatar ||
                editedData?.profile_image ||
                getArtistPayload(editedData)?.image_url ||
                getArtistPayload(editedData)?.image ||
                null;
              return imgSrc ? (
                <div className={styles.profileImageWrap}>
                  <img
                    src={imgSrc}
                    alt="Profile"
                    className={styles.profileImage}
                  />
                </div>
              ) : (
                <div className={styles.profileImageWrap}>
                  <div className={styles.profileImagePlaceholder}>
                    {(getUserName(editedData) || "U")[0].toUpperCase()}
                  </div>
                </div>
              );
            })()}

            <div className={styles.detailSection}>
              <h5>User Details</h5>
              {renderKeyValueRows(editedData, USER_HIDDEN_KEYS)}
            </div>

            {/* Artist Details */}
            {getArtistPayload(editedData) ? (
              <div className={styles.detailSection}>
                <h5>Artist Details</h5>
                {renderKeyValueRows(getArtistPayload(editedData), ARTIST_HIDDEN_KEYS)}
              </div>
            ) : null}

            {(() => {
              const shop = getArtistShopPayload(editedData);
              if (!shop || typeof shop !== "object" || Array.isArray(shop)) return null;
              return (
                <div className={styles.detailSection}>
                  <h5>Shop Details</h5>
                  {renderShopDetails(shop)}
                </div>
              );
            })()}

            {shopApprovalStatus === "loading" ? (
              <div className={styles.shopActionRow}>
                <span style={{ color: "#aaa", fontSize: "13px" }}>Checking approval status...</span>
              </div>
            ) : showShopActionButtons() ? (
              <div className={styles.shopActionRow}>
                <button
                  type="button"
                  className={styles.approveBtn}
                  disabled={shopActionLoading}
                  onClick={() => handleArtistShopAction("approve", editedData)}
                >
                  {shopActionLoading ? "Please wait..." : "✓ Approve"}
                </button>
                <button
                  type="button"
                  className={styles.rejectBtn}
                  disabled={shopActionLoading}
                  onClick={() => handleArtistShopAction("reject", editedData)}
                >
                  {shopActionLoading ? "Please wait..." : "✕ Reject"}
                </button>
              </div>
            ) : null}
          </div>
        </ModalContainer>
      ) : (
        ""
      )}

      <div className={styles.headerContainer}>
        <SubTitle text={"User Management"} />
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className={styles.mainTable}>
        <Table data={filteredData} columns={columns} indication={indication} />
      </div>
    </div>
  );
};

export default UserManagementScreen;
